/**
 * Image Optimization Utility
 * 
 * Provides optimized image URLs using Vercel's Image Optimization API
 * when running on Vercel, or falls back to original URLs otherwise.
 */

export interface ImageOptimizationOptions {
  width?: number;
  quality?: number;
}

/**
 * Get an optimized image URL
 * 
 * Note: Vercel's /_vercel/image endpoint is only available for Next.js apps.
 * For Vite/React apps, we return the original URL. Images from Vercel Blob Storage
 * are already optimized and served via CDN, so additional optimization may not be needed.
 * 
 * @param originalUrl - The original image URL
 * @param options - Optimization options (width, quality) - currently unused for non-Next.js
 * @returns Original image URL (optimization handled by CDN)
 */
export function getOptimizedImageUrl(
  originalUrl: string | undefined,
  options: ImageOptimizationOptions = {}
): string {
  if (!originalUrl) return '';
  
  // For Vite/React apps on Vercel, the /_vercel/image endpoint is not available
  // Vercel Blob Storage URLs are already served via CDN and are optimized
  // Return the original URL - the browser and CDN will handle caching
  return originalUrl;
  
  // Future: If you want to add image optimization, you could:
  // 1. Create a custom API route at /api/image-optimize
  // 2. Use a service like Cloudinary or Imgix
  // 3. Use a library like sharp in a serverless function
}

/**
 * Preset sizes for common use cases
 */
export const IMAGE_SIZES = {
  thumbnail: { width: 200, quality: 70 },
  card: { width: 400, quality: 75 },
  feed: { width: 600, quality: 80 },
  detail: { width: 1200, quality: 85 },
  avatar: { width: 100, quality: 80 },
} as const;

/**
 * Get a thumbnail-optimized image URL
 */
export function getThumbnailUrl(url: string | undefined): string {
  return getOptimizedImageUrl(url, IMAGE_SIZES.thumbnail);
}

/**
 * Get a card-optimized image URL (for feed cards)
 */
export function getCardImageUrl(url: string | undefined): string {
  return getOptimizedImageUrl(url, IMAGE_SIZES.card);
}

/**
 * Get a feed-optimized image URL
 */
export function getFeedImageUrl(url: string | undefined): string {
  return getOptimizedImageUrl(url, IMAGE_SIZES.feed);
}

/**
 * Get a detail-optimized image URL (for full-screen views)
 */
export function getDetailImageUrl(url: string | undefined): string {
  return getOptimizedImageUrl(url, IMAGE_SIZES.detail);
}

/**
 * Get an avatar-optimized image URL
 */
export function getAvatarUrl(url: string | undefined): string {
  return getOptimizedImageUrl(url, IMAGE_SIZES.avatar);
}
